import { useMemo, useState } from 'react'
import { Field, NumberInput, ResultStat, Select, Toggle } from './ui'

const UNIT_VOLUME: Record<string, number> = {
  '14': 2.0,
  '12': 2.25,
  '10': 2.5,
  '8': 3.0,
}

const GAUGE_OPTIONS = [
  { label: '14 AWG (2.00 cu. in.)', value: '14' as const },
  { label: '12 AWG (2.25 cu. in.)', value: '12' as const },
  { label: '10 AWG (2.50 cu. in.)', value: '10' as const },
  { label: '8 AWG (3.00 cu. in.)', value: '8' as const },
]

function NecBoxFill() {
  const [gauge, setGauge] = useState('12')
  const [conductors, setConductors] = useState(4)
  const [clamps, setClamps] = useState(true)
  const [fittings, setFittings] = useState(false)
  const [devices, setDevices] = useState(1)
  const [grounds, setGrounds] = useState(1)

  const result = useMemo(() => {
    const unit = UNIT_VOLUME[gauge] ?? 2.25
    const conductorAllow = Math.max(0, conductors)
    const clampAllow = clamps ? 1 : 0
    const fittingAllow = fittings ? 1 : 0
    const deviceAllow = Math.max(0, devices) * 2
    const groundAllow = grounds > 0 ? 1 : 0
    const totalAllow = conductorAllow + clampAllow + fittingAllow + deviceAllow + groundAllow
    const requiredCuIn = totalAllow * unit
    const requiredCm3 = requiredCuIn * 16.387064
    return {
      unit,
      conductorAllow,
      clampAllow,
      fittingAllow,
      deviceAllow,
      groundAllow,
      totalAllow,
      requiredCuIn,
      requiredCm3,
    }
  }, [gauge, conductors, clamps, fittings, devices, grounds])

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <div className="space-y-4">
        <Field label="Primary Conductor Wire Gauge" hint="NEC Table 314.16(B)">
          <Select value={gauge} onChange={setGauge} options={GAUGE_OPTIONS} />
        </Field>
        <Field label="Number of Conductors / Wires" hint="passing through">
          <NumberInput value={conductors} onChange={setConductors} min={0} max={50} />
        </Field>
        <div className="grid gap-3 sm:grid-cols-2">
          <Toggle checked={clamps} onChange={setClamps} label="Internal Clamps" />
          <Toggle checked={fittings} onChange={setFittings} label="Support Fittings" />
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Devices / Yokes" hint="×2 allowance each">
            <NumberInput value={devices} onChange={setDevices} min={0} max={20} />
          </Field>
          <Field label="Grounding Wires" hint="1 total allowance">
            <NumberInput value={grounds} onChange={setGrounds} min={0} max={20} />
          </Field>
        </div>
      </div>

      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <ResultStat label="Required Volume" value={`${result.requiredCuIn.toFixed(2)}`} sub="cubic inches" accent="text-amber-600" />
          <ResultStat label="Metric Volume" value={`${result.requiredCm3.toFixed(1)}`} sub="cubic cm" />
        </div>
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
          <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
            Allowance Breakdown
          </div>
          <table className="w-full font-mono text-sm">
            <tbody className="divide-y divide-slate-200">
              <Row label="Conductors" value={`${result.conductorAllow}`} />
              <Row label="Internal clamps" value={`${result.clampAllow}`} />
              <Row label="Support fittings" value={`${result.fittingAllow}`} />
              <Row label="Devices (×2)" value={`${result.deviceAllow}`} />
              <Row label="Grounding" value={`${result.groundAllow}`} />
              <Row label="Total allowances" value={`${result.totalAllow}`} strong />
              <Row label="Unit volume" value={`${result.unit.toFixed(2)} in³`} />
            </tbody>
          </table>
        </div>
        <p className="text-xs leading-relaxed text-slate-500">
          Formula: <span className="font-mono text-slate-700">Required Volume = Total Allowances × Unit Volume</span>.
          Reference: NEC Article 314.16(A)/(B). Always verify against the latest adopted code edition.
        </p>
      </div>
    </div>
  )
}

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <tr>
      <td className={`py-1.5 ${strong ? 'font-semibold text-slate-900' : 'text-slate-500'}`}>{label}</td>
      <td className={`py-1.5 text-right ${strong ? 'font-bold text-slate-900' : 'text-slate-700'}`}>{value}</td>
    </tr>
  )
}

export default NecBoxFill
